import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { Save } from "lucide-react";
import { useAdminGetRightsSWR, useAdminCreateRole, useAdminGetRolesDefinitionsSWR } from "@/services/roleService";
import { useAdminGetSkillsSWR } from "@/services/skillService";
import { useAdminGetCompetitionsSWR } from "@/services/competitionService";

const initialState = {
    apiPayload: {
        name: '',
        type: '',
        rights: [],
        skill_id: '',
        competition_id: '',
        hidden: false,
    },
    error_message: "",
    isLoading: false
}



const ACTIONS = {
    SET_API_PAYLOAD_FIELD: 'setApiPayloadField',
    ADD_API_PAYLOAD_RIGHT: 'addApiPayloadRight',
    REMOVE_API_PAYLOAD_RIGHT: 'removeApiPayloadRight',
    SET_ERROR_MESSAGE: 'setErrorMessage',
    SET_IS_LOADING: 'setIsLoading',
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_API_PAYLOAD_FIELD:
            return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload }, error_message: null }
        case ACTIONS.ADD_API_PAYLOAD_RIGHT:
            return { ...state, apiPayload: { ...state.apiPayload, rights: [...state.apiPayload.rights, action.payload] } }
        case ACTIONS.REMOVE_API_PAYLOAD_RIGHT:
            return { ...state, apiPayload: { ...state.apiPayload, rights: state.apiPayload.rights.filter((right) => right != action.payload) } }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, error_message: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}



export default function Add() {

    const [ , setPageTitle, , setShowBackButton] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)

    const navigate = useNavigate()
    const location = useLocation()

    const {data: rights } = useAdminGetRightsSWR()
    const {data: rolesDefinitions } = useAdminGetRolesDefinitionsSWR()
    const {data: skills } = useAdminGetSkillsSWR()
    const {data: competitions } = useAdminGetCompetitionsSWR()

    const adminCreateRole = useAdminCreateRole()



    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Ajout d'un rôle")
        setShowBackButton(true)

        return () => {
            setPageTitle("Titre")
            setShowBackButton(false)
        }
    }, [setPageTitle, setShowBackButton])


    
    /* Gestion des changements de champs du formulaire a l'exception des droits */
    const handleChange = (e) => {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
        dispatch({ type: ACTIONS.SET_API_PAYLOAD_FIELD, subtype: e.target.name, payload: e.target.type == 'checkbox' ? e.target.checked : e.target.value })
    }



    /* Soumission du formulaire */
    const handleSubmit = async (e) => {
        e.preventDefault()

        if(!state.apiPayload.name || !state.apiPayload.type ) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Veuillez remplir tous les champs obligatoires" });
            return 
        }

        const payload = {
            ...state.apiPayload
        }

        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {
            await adminCreateRole(payload)
            navigate(location.state?.from ?? "/admin/roles")
        } catch (error) {
            console.error('admin/roles/add::handleSubmit::error', error)
            if (error?.response?.status === 412) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Erreur de validation en base de données (nom déjà utilisé, contrainte non respectée)" });
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }




    return (
        <>
            <h2 className="text-secondary text-lg flex-none content-center mb-4">
                Paramètres du rôle
            </h2>

            <form className="flex flex-col gap-4" onSubmit={(e) => handleSubmit(e)}>

                {/* Ligne : nom et type */}
                <div className="md:w-[60%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="name">
                            Nom <span className="text-red-500">*</span>
                        </label>
                        <input type="text" className="w-full border border-secondary rounded-md"
                            id="name" name="name" 
                            value={state.apiPayload.name} 
                            onChange={handleChange}
                            required
                            />
                    </div>
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="type">
                            Type <span className="text-red-500">*</span>
                        </label>
                        <select className="border border-secondary rounded-md text-base"
                            id="type" name="type" 
                            value={state.apiPayload.type} 
                            onChange={handleChange}
                            required
                        >
                            {rolesDefinitions && Object.keys(rolesDefinitions)?.map((role, i) => {
                                return (
                                    <option key={i} value={role}>
                                        {role}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                    
                </div>
                
                {/* Ligne : gestion du métier associé */}
                <div className="md:w-[60%] flex flex-row gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="skill_id">
                            Métier
                        </label>
                        <select className="border border-secondary rounded-md text-base"
                            id="skill_id" name="skill_id" 
                            value={state.apiPayload.skill_id} 
                            onChange={handleChange}
                            required
                        >
                            <option value="">-</option>
                            {skills?.sort((a, b) => (a.name > b.name) ? 1 : -1).map((skill, i) => {
                                return (
                                    <option key={i} value={skill.id}>
                                        {skill.code} - {skill.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                </div>

                {/* Ligne : gestion de la competition associée */}
                <div className="md:w-[60%] flex flex-row gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="competition_id">
                            Compétition
                        </label>
                        <select className="border border-secondary rounded-md text-base"
                            id="competition_id" name="competition_id" 
                            value={state.apiPayload.competition_id} 
                            onChange={handleChange}
                            required
                        >
                            <option value="">-</option>
                            {competitions.map((competition, i) => {
                                return (
                                    <option key={i} value={competition.id}>
                                        {competition.code} - {competition.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                </div>

                {/* Ligne : Caché */}
                <div className="md:w-[60%] flex flex-row gap-5 content-stretch">
                    <div className="flex flex-row gap-2 flex-auto">
                        <input
                            type="checkbox"
                            className="h-6 w-6 border border-secondary rounded-md content-center"
                            id="hidden" name="hidden" 
                            onChange={handleChange}
                            checked={state.apiPayload.hidden}
                        />
                        <label className="text-primary text-base" htmlFor="competition_id">
                            Rôle caché
                        </label>
                    </div>
                </div>
            
                {/* Ligne : gestion des droits */}
                <span className="text-base">Droits associés à ce rôle :</span>
                <div className="w-full flex flex-row flex-wrap gap-5 content-stretch">
                    {rights?.map((right, i) => {
                        return (
                            <div key={i} 
                                className={`flex flex-row items-center gap-2 border border-secondary rounded-md px-2 py-2 text-base
                                    ${ !state.apiPayload.rights.includes(right) ? "border-dashed" : ""}`}
                            >
                                <input type="checkbox" className="h-6 w-6 border border-secondary rounded-md content-center"
                                    id={right} name={right} 
                                    checked={state.apiPayload.rights.includes(right)} 
                                    onChange={(e) => {
                                        if(e.target.checked) {
                                            dispatch({ type: ACTIONS.ADD_API_PAYLOAD_RIGHT, payload: right })
                                        } else {
                                            dispatch({ type: ACTIONS.REMOVE_API_PAYLOAD_RIGHT, payload: right })
                                        }
                                    }}
                                />
                                <label className="text-primary text-base content-center" htmlFor={right}>
                                    {right}
                                </label>
                            </div>
                        )
                    })}
                </div>

                {/* Fin : sauvegarder et message d'erreur */}
                <div className="w-[50%] gap-5 flex flex-row items-center">
                    
                    <button className="flex gap-2 items-center border border-blue-500 rounded-md px-2 py-1 text-base hover:bg-blue-200"
                        onClick={(e) => handleSubmit(e)}
                        disabled={state.isLoading}
                    >
                        <Save size={24} className="text-primary" />
                        Sauvegarder
                    </button>
                    
                    {
                        state?.error_message && <span className="text-red-700 flex-none content-center mr-4">{state.error_message}</span>
                    }

                </div>
            </form>
        </>
    )
}